<template>
  <div class="pp-table-wrap">
    <div class="pp-table">
      <div class="pp-head pp-grid">
        <span>Patient</span>
        <span class="pp-right">Amount</span>
        <span>Applied to</span>
        <span>Channel</span>
        <span>Paid</span>
        <span>Open Dental</span>
      </div>

      <div v-for="r in rows" :key="r.PaymentID" class="pp-row pp-grid">
        <div class="pp-patient">
          <span class="pp-avatar">{{ initials(r.PatientName) }}</span>
          <div class="pp-patient-text">
            <div class="pp-name">{{ r.PatientName || ('PatNum ' + r.PatNum) }}</div>
            <div class="pp-chart">#{{ r.PatNum }}</div>
          </div>
        </div>

        <div class="pp-amount" :class="{ 'pp-amount--voided': isVoided(r) }">
          {{ money(collected(r)) }}
          <!-- A partial approval collected LESS than the office asked for. The
               row is real money and counts toward the day's total, but the gap
               has to be visible or the office reconciles against a number that
               silently doesn't match the balance they tried to take. -->
          <div v-if="isPartial(r)" class="pp-amount-note" :title="'Requested ' + money(r.TotalCharged)">
            partial · of {{ money(r.TotalCharged) }}
          </div>
        </div>

        <div>
          <span class="pp-pill pp-pill--type">{{ appliedTo(r) }}</span>
        </div>

        <div class="pp-channel">
          <i class="fa" :class="channel(r).icon" :style="{ color: channel(r).color }" aria-hidden="true"></i>
          <span>{{ channel(r).label }}</span>
        </div>

        <div>
          <div class="pp-paid-date">{{ paidDate(r.PaidAt) }}</div>
          <div class="pp-paid-time">{{ paidTime(r.PaidAt) }}</div>
        </div>

        <div>
          <span class="pp-pill" :class="'pp-pill--' + statusPill(r).tone">
            <i class="fa" :class="statusPill(r).icon" aria-hidden="true"></i>
            {{ statusPill(r).label }}
          </span>
          <div v-if="r.MoolahTransactionId" class="pp-txn" :title="r.MoolahTransactionId">
            {{ shortTxn(r.MoolahTransactionId) }}
          </div>
        </div>
      </div>

      <div v-if="!rows.length" class="pp-empty">
        <div class="pp-empty-title">{{ emptyTitle }}</div>
        <div class="pp-empty-sub">{{ emptySub }}</div>
      </div>

      <div v-if="rows.length" class="pp-total pp-grid">
        <span class="pp-total-label">Total — {{ collectedCount }} payment{{ collectedCount === 1 ? '' : 's' }}</span>
        <span class="pp-total-figure">{{ money(total) }}</span>
        <span class="pp-total-note">{{ footNote }}</span>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { collectedAmountOf, isPartialApproval } from '../../../../utils/paymentsCsv';

export default {
  name: 'PaymentTable',
  props: {
    rows: { type: Array, default: () => [] },
    total: { type: [Number, String], default: 0 },
    // How many of `rows` the `total` actually covers. Differs from rows.length
    // when voided rows are listed but excluded from the money — the label and
    // the figure beside it have to describe the same set.
    count: { type: Number, default: null },
    footNote: { type: String, default: '' },
    emptyTitle: { type: String, default: 'No payments match' },
    emptySub: { type: String, default: 'Try a wider date range or clear your filters.' },
    // Injected so the table formats in the SITE's timezone using the same
    // helper the rest of the app uses, rather than re-deriving it here.
    formatDateTime: { type: Function, required: true },
  },
  computed: {
    collectedCount() {
      return this.count === null ? this.rows.length : this.count;
    },
  },
  methods: {
    money(n) {
      const v = Number(n) || 0;
      return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    // "Lecaud, Renee" -> RL ; "Rugesh Shah" -> RS. Handles both shapes because
    // PatientName arrives in either depending on how the row was created.
    initials(name) {
      if (!name) return '?';
      const clean = String(name).replace(',', ' ').trim().split(/\s+/).filter(Boolean);
      if (!clean.length) return '?';
      if (clean.length === 1) return clean[0].slice(0, 2).toUpperCase();
      return (clean[1][0] + clean[0][0]).toUpperCase();
    },

    appliedTo(r) {
      return r.PaymentIntent === 'prepayment' ? 'Treatment plan' : 'Balance';
    },

    // Channel is inferred server-side from the card entryMode. Only two are
    // distinguishable in our data — a keyed card reads as a link, which is a
    // known limitation of that inference, so we don't over-claim here.
    channel(r) {
      if (r.channel === 'terminal') return { label: 'In-office', icon: 'fa-desktop', color: '#0f7259' };
      if (r.channel === 'link') return { label: 'Payment link', icon: 'fa-link', color: '#f59e0b' };
      return { label: '—', icon: 'fa-question-circle', color: '#9ca4a2' };
    },

    isVoided(r) {
      return r && r.Status === 'voided';
    },

    // Shared with the CSV export so the sheet and the screen can never disagree
    // about what a row collected.
    collected(r) {
      const n = collectedAmountOf(r);
      return n === null ? 0 : n;
    },

    isPartial(r) {
      return isPartialApproval(r);
    },

    // A charge voided at the terminal after capture was reversed at the
    // processor, so Open Dental correctly holds no record of it. Showing the
    // OD-sync chip here would render "Not in OD" in alarming red for a row
    // where that is the RIGHT outcome — which is exactly the false alarm that
    // paged us on 2026-08-17. The void supersedes the sync state.
    statusPill(r) {
      if (this.isVoided(r)) return { label: 'Voided', tone: 'muted', icon: 'fa-ban' };
      return this.sync(r.OdSyncStatus);
    },

    sync(status) {
      switch (status) {
        case 'synced': return { label: 'Synced to OD', tone: 'ok', icon: 'fa-check-circle' };
        case 'pending': return { label: 'Confirming…', tone: 'warn', icon: 'fa-clock-o' };
        case 'unconfirmed': return { label: 'Not in OD', tone: 'bad', icon: 'fa-exclamation-triangle' };
        // 'failed' is a real stored state (the OD write threw). It must read as
        // a problem, not as an unknown grey chip.
        case 'failed': return { label: 'Write failed', tone: 'bad', icon: 'fa-times-circle' };
        default: return { label: status || 'Unknown', tone: 'muted', icon: 'fa-question-circle' };
      }
    },

    paidDate(v) {
      if (!v) return '—';
      const s = this.formatDateTime(v);
      return s ? String(s).split(' ')[0] : '—';
    },

    paidTime(v) {
      if (!v) return '';
      const s = this.formatDateTime(v);
      if (!s) return '';
      const parts = String(s).split(' ');
      return parts.slice(1).join(' ');
    },

    shortTxn(id) {
      const s = String(id);
      return s.length > 15 ? s.slice(0, 13) + '…' : s;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

.pp-table-wrap { overflow-x: auto; }

// Min-width keeps the columns honest; the wrapper scrolls rather than letting
// the page body scroll sideways.
.pp-table { min-width: 1020px; }

.pp-grid {
  display: grid;
  grid-template-columns: minmax(190px, 1.5fr) 120px 150px 150px minmax(150px, 1fr) 170px;
  gap: 12px;
  align-items: center;
}

.pp-head {
  padding: 12px 18px;
  background: $pp-surface-sunken;
  border-bottom: 1px solid $pp-border;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: $pp-text-muted;
}

.pp-right { text-align: right; }

.pp-row {
  padding: 13px 18px;
  border-bottom: 1px solid $pp-border;

  &:hover { background: $pp-surface-sunken; }
}

.pp-patient {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}

.pp-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: $pp-brand-subtle;
  color: $pp-brand;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
  flex: none;
}

.pp-patient-text { min-width: 0; }

.pp-name {
  font-weight: 700;
  font-size: 14px;
  color: $pp-brand;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pp-chart {
  font-size: 11.5px;
  color: $pp-text-subtle;
  font-family: $pp-font-mono;
}

.pp-amount {
  text-align: right;
  font-family: $pp-font-display;
  font-weight: 800;
  font-size: 16px;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  color: $pp-text;
}

// Partial approval: the collected figure keeps the amount styling (it IS the
// money) and the requested amount sits under it, quiet — enough to explain the
// gap when the office is matching this row against a balance, not enough to
// compete with the number that actually counts.
.pp-amount-note {
  margin-top: 2px;
  font-family: $pp-font-display;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0;
  color: $pp-text-muted;
  white-space: nowrap;
}

// Voided: the charge was reversed, so the amount is struck and muted. It stays
// on screen — an office reconciling its day needs to see the void rather than
// find money quietly absent — but it reads as "not collected" at a glance.
.pp-amount--voided {
  text-decoration: line-through;
  color: $pp-text-muted;
  font-weight: 600;
}

.pp-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  padding: 4px 10px;
  border-radius: $pp-radius-pill;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid transparent;

  &--type {
    background: $pp-surface-sunken;
    border-color: $pp-border;
    color: $pp-text-strong;
    font-weight: 600;
  }
  &--ok { background: $pp-brand-subtle; border-color: $pp-brand-border; color: $pp-brand-deep; }
  &--warn { background: $pp-amber-subtle; border-color: $pp-amber-border; color: $pp-amber-text; }
  &--bad { background: $pp-red-subtle; border-color: $pp-red-border; color: $pp-red-text; }
  &--muted { background: $pp-surface-sunken; border-color: $pp-border; color: $pp-text-muted; }
}

.pp-channel {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 600;
  color: $pp-text-strong;
}

.pp-paid-date { font-size: 13.5px; font-weight: 600; color: $pp-text-strong; }
.pp-paid-time { font-size: 11.5px; color: $pp-text-subtle; margin-top: 1px; }

.pp-txn {
  font-size: 11px;
  color: $pp-text-subtle;
  font-family: $pp-font-mono;
  margin-top: 4px;
}

.pp-empty {
  padding: 56px 20px;
  text-align: center;
}

.pp-empty-title { font-weight: 700; color: $pp-text-strong; font-size: 15px; }
.pp-empty-sub { font-size: 13.5px; color: $pp-text-muted; margin-top: 4px; }

.pp-total {
  padding: 16px 18px;
  background: $pp-surface-sunken;
}

.pp-total-label { font-size: 13px; font-weight: 700; color: $pp-text; }

.pp-total-figure {
  text-align: right;
  font-family: $pp-font-display;
  font-weight: 800;
  font-size: 19px;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: $pp-text;
}

.pp-total-note {
  grid-column: 3 / span 4;
  font-size: 12.5px;
  color: $pp-text-muted;
  text-align: right;
}
</style>
